import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '../../../core/app.service';
import { GlobalBookingSearchService } from '../../../screens/bookings/global-booking-search.service';
import { IconComponent } from '../../icon/icon.component';
import { LangSwitcherComponent } from '../../widgets/lang-switcher/lang-switcher.component';
import { openDrawerDialog } from '../../widgets/drawer/drawer.helpers';
import {
  NetworkHelpDrawerComponent, NetworkHelpDrawerData,
} from '../../../screens/network-help/network-help-drawer.component';
import { pageHelpKeyForUrl } from '../../../screens/network-help/network-help.data';
import { PageTourService } from '../../../core/page-tour.service';
import { HelpContextService } from '../../../core/help-context.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [IconComponent, LangSwitcherComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  readonly app = inject(AppService);
  readonly search = inject(GlobalBookingSearchService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly pageTour = inject(PageTourService);
  private readonly helpCtx = inject(HelpContextService);
  readonly crumbs = input<readonly string[]>([]);

  /** Bound to the search input. */
  readonly query = signal('');

  /** The Network-help key for the current route, or null when the page has
   *  no contextual help (drives the topbar Help button's visibility). */
  readonly helpKey = signal<string | null>(pageHelpKeyForUrl(this.router.url));
  /** The route key when the current page has a guided tour (drives the Tour
   *  button beside Help). Null on pages without one. */
  readonly tourKey = signal<string | null>(this.resolveTourKey(this.router.url));

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  constructor() {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe((e) => {
      this.helpKey.set(pageHelpKeyForUrl(e.urlAfterRedirects));
      this.tourKey.set(this.resolveTourKey(e.urlAfterRedirects));
    });
  }

  private resolveTourKey(url: string): string | null {
    const seg = (url.split('?')[0].split('#')[0].split('/')[1] || '').toLowerCase();
    return this.pageTour.hasTour(seg) ? seg : null;
  }

  /** Launch the current page's guided tour. */
  openTour(): void {
    const key = this.tourKey();
    if (key) this.pageTour.start(key);
  }

  /** Open the contextual page-help drawer for the current Network route. */
  openHelp(): void {
    const key = this.helpKey();
    if (!key) return;
    openDrawerDialog<NetworkHelpDrawerComponent, NetworkHelpDrawerData>(
      this.dialog, NetworkHelpDrawerComponent,
      { width: '560px', data: { key, subKey: this.helpCtx.subKey() } },
    );
  }

  toggleTheme(): void {
    this.app.setTheme(this.app.theme() === 'dark' ? 'light' : 'dark');
  }

  /** Resolve the query: unique hit → booking detail, else → All Bookings. */
  onSubmit(event: Event): void {
    event.preventDefault();
    const q = this.query().trim();
    if (!q) return;
    this.search.search(q);
  }

  /** ⌘K / Ctrl-K focuses the global search — matches the hint on the pill. */
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
      this.searchInput?.nativeElement.select();
    }
  }
}
